import { useState } from 'react';
import { Err } from '../ui.js';
import { checkSendingDomains, syncSendingDomains, ApiError } from '../api.js';
import type { IdentityTruth } from '../api.js';

// WHAT RESEND ACTUALLY SAYS, RATHER THAN WHAT YOU TICKED.
//
// Verification was a checkbox the operator set by hand and the send engine then
// trusted completely — the one fact in the app a human should never be the
// source of. Tick it early and cold mail lands in spam silently for a quarter.
// Forget to tick it and the engine refuses forever for a reason that stopped
// being true.
//
// This asks. It also demotes, which is the half that matters: a domain can lose
// verification when its DNS changes and nothing else here would ever notice.

export function DomainCheck() {
  const [rows, setRows] = useState<IdentityTruth[] | null>(null);
  const [domains, setDomains] = useState<{ name: string; status: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(sync: boolean) {
    setBusy(true); setError(null); setNote(null);
    try {
      if (sync) {
        const r = await syncSendingDomains();
        setRows(r.identities); setNote(r.note);
      } else {
        const r = await checkSendingDomains();
        setRows(r.identities); setDomains(r.domains);
        setNote(r.domains.length
          ? null
          : 'Resend has no domains at all yet. Add one there first.');
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  const orphan = rows?.filter((r) => !r.resend_domain) ?? [];

  return (
    <div className="domaincheck">
      <div className="actions">
        <button disabled={busy} onClick={() => void run(false)}>
          {busy ? 'Asking Resend…' : 'Check with Resend'}
        </button>
        {rows && rows.some((r) => !r.agrees) && (
          <button className="primary" disabled={busy} onClick={() => void run(true)}>
            Match Resend
          </button>
        )}
      </div>

      <Err>{error}</Err>
      {note && <p className="meta">{note}</p>}

      {domains.length > 0 && (
        <p className="meta">
          Verified in Resend: {domains.filter((d) => d.status === 'verified')
            .map((d) => d.name).join(', ') || 'none yet'}
        </p>
      )}

      {rows?.map((r) => (
        <div className="row" key={r.id}>
          <span className="rname">{r.from_email}</span>
          <span className={`flag ${r.truth === 'verified' ? 'flag-ok' : 'flag-warn'}`}>
            {r.truth}
          </span>
          {!r.agrees && (
            <span className="meta">you had it as {r.believed}</span>
          )}
        </div>
      ))}

      {/* The failure that actually happens: the address you want to send from
          belongs to a domain Resend has never heard of. Saying "unverified"
          would send the operator to look for a verification that was never
          started. */}
      {orphan.length > 0 && (
        <p className="warnbox">
          <strong>Not in Resend at all:</strong>{' '}
          {orphan.map((r) => r.from_email.split('@')[1]).join(', ')}. Add the domain
          in Resend → Domains, publish the DNS records it gives you, then check again.
          Nothing can send from it until that is done.
        </p>
      )}
    </div>
  );
}
